import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["alignment", "checked", "children", "childWrapperId", "description", "descriptionId", "descriptionLayout", "disabled", "indicator", "inline", "input", "onChange", "readOnly", "size", "labelStyles", "title"];
import { forwardRef } from 'react';
import styled, { css } from 'foundations-theming/utils';
import { isFirefox } from '../../utils/BrowserTest';
import { isModifierPressed } from '../../utils/DomEvents';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { MIN_HEIGHT } from '../../../constants/nonTokenisedValues';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ALIGNMENT_OPTIONS = {
  start: 'flex-start',
  baseline: 'baseline',
  center: 'center',
  end: 'flex-end'
};
export const SIZE_OPTIONS = {
  default: '',
  sm: 'sm'
};
const Wrapper = styled.div.withConfig({
  displayName: "ToggleInputWrapper__Wrapper"
})(props => {
  const {
    $disabled,
    $inline,
    $readOnly,
    $shorthandSize,
    $stackContents
  } = props;
  const theme = props.theme;
  const minHeight = MIN_HEIGHT.TOGGLE_INPUT_WRAPPER;
  return Object.assign({
    minHeight,
    alignItems: $stackContents ? 'flex-start' : 'center',
    fontSize: $shorthandSize === 'sm' ? '92%' : '1rem',
    display: 'flex',
    position: 'relative',
    '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
      '&::after': {
        minHeight,
        content: '" "',
        display: 'inline-block'
      }
    }
  }, $stackContents && {
    flexDirection: 'column',
    '&::after': {
      display: 'none'
    },
    '& + &': {
      marginTop: theme.space[400]
    },
    '&:first-child': {
      marginTop: 0
    }
  }, $shorthandSize === 'sm' && {
    marginTop: 0
  }, $readOnly && {
    '& > label > span:first-of-type > span:first-of-type': {
      backgroundColor: `${theme.color['fill-field-disabled']}`,
      borderColor: `${theme.color['border-core-disabled']}`,
      cursor: 'auto',
      color: `${theme.color['icon-core-subtle']}`
    }
  }, $disabled && {
    '& > label > span:first-of-type > span:first-of-type': {
      backgroundColor: `${theme.color['fill-field-disabled']}`,
      borderColor: theme.color['border-core-disabled'],
      color: `${theme.color['icon-core-disabled']}`
    }
  }, $inline && {
    minHeight,
    display: 'inline-flex',
    '& + &': {
      marginLeft: '1.25em'
    }
  });
});
const Label = styled.label.withConfig({
  displayName: "ToggleInputWrapper__Label"
})([".hs-private.hs-private.hs-private &{cursor:pointer;display:block;max-width:100%;line-height:normal;", " ", " ", " ", "}"], props => props.$disabled && 'cursor: not-allowed;', props => props.$readOnly && 'cursor: default;', props => !props.$disabled && !props.$readOnly && `&:hover > span:first-of-type > span:first-of-type { background-color: ${props.theme.color['fill-tertiary-default']};}`, ({
  $cssStyles
}) => $cssStyles && $cssStyles);
const InnerSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__InnerSpan"
})([".hs-private.hs-private.hs-private &{display:flex;align-items:", ";}"], props => ALIGNMENT_OPTIONS[props.$alignment]);
export const InnerTextSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__InnerTextSpan"
})(props => {
  const {
    $disabled,
    $shorthandSize,
    $readonly
  } = props;
  const theme = props.theme;
  return Object.assign({
    position: 'relative',
    lineHeight: 'normal',
    maxWidth: '100%',
    fontSize: theme.space[400],
    paddingLeft: theme.space[300]
  }, $shorthandSize === 'sm' && {
    fontSize: theme.typography['body-100'].fontSize,
    paddingLeft: theme.space[200],
    top: 'auto'
  }, $disabled && {
    color: theme.color['text-core-disabled']
  }, $readonly && {
    color: theme.color['text-core-subtle']
  });
});
const DescriptionSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__DescriptionSpan"
})([".hs-private.hs-private.hs-private &{margin-left:", ";max-width:100%;position:relative;", "}"], props => props.$stackContents ? '0' : '1ch', props => props.$stackContents && css(["padding-left:", ";"], props.$shorthandSize === 'sm' ? '25px' : '32px'));
export const ToggleInputWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      alignment = 'start',
      checked,
      children,
      childWrapperId,
      description,
      descriptionId,
      descriptionLayout,
      disabled,
      indicator,
      inline,
      input,
      onChange,
      readOnly,
      size,
      labelStyles = {},
      title
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const shorthandSize = size && toShorthandSize(size);
  const stackContents = Boolean(description && descriptionLayout === 'vertical');

  // Label styles
  const labelCssStyles = useMapTokenPropsToCSS(labelStyles);
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
    $disabled: disabled,
    $inline: inline,
    $readOnly: readOnly,
    $shorthandSize: shorthandSize,
    $stackContents: stackContents,
    title: title,
    ref: ref,
    children: [/*#__PURE__*/_jsx(Label, {
      $disabled: disabled,
      $readOnly: readOnly,
      $cssStyles: labelCssStyles,
      onClick: evt => {
        if (isFirefox() && isModifierPressed(evt) && onChange) {
          onChange(Object.assign({}, evt, {
            type: 'change',
            target: {
              checked: !checked
            }
          }));
        }
      },
      children: /*#__PURE__*/_jsxs(InnerSpan, {
        $alignment: alignment,
        children: [input, indicator, children && /*#__PURE__*/_jsx(InnerTextSpan, {
          $shorthandSize: shorthandSize,
          $disabled: disabled,
          $readonly: readOnly,
          id: childWrapperId,
          children: children
        })]
      })
    }), description && /*#__PURE__*/_jsx(DescriptionSpan, {
      $shorthandSize: shorthandSize,
      $stackContents: stackContents,
      id: descriptionId,
      children: description
    })]
  }));
});
ToggleInputWrapper.displayName = 'ToggleInputWrapper';